import type { Action } from "../ws";
import type { TerrainFeatureSetup } from "./TerrainFeature";

export const rollD3 = () => 1 + Math.floor(Math.random() * 3);
export const rollD6 = () => 1 + Math.floor(Math.random() * 6);

export const rollND3 = (n: number) => {
  const arr: number[] = new Array<number>(n);

  for(let i = 0; i < n; ++i) arr[i] = rollD3();

  return arr;
};

export const rollND6 = (n: number) => {
  const arr: number[] = new Array<number>(n);

  for(let i = 0; i < n; ++i) arr[i] = rollD6();

  return arr;
};

const GameStage = ["firstTurn", "twist"] as const;
export type GameStage = (typeof GameStage)[number];

const GameState = ["begin", "round"] as const;
export type GameState = (typeof GameState)[number];

export interface GameRuleState {
  army1p: string;
  army2p: string;
  firstTurn: number;
  rollOff: number[];
  rolls: number[];
  round: number;
  stage: GameStage;
  state: GameState;
  terrainFeatures: TerrainFeatureSetup[];
  turn: number;
  underdog: number;
  victoryPoints: number[];
}

export type ActionReturn<S extends boolean, R = void> = S extends true ? { [key: string]: unknown } | null : Promise<R>;

const initState = (state: [string, string]) => ({
  army1p:        state[0],
  army2p:        state[1],
  firstTurn:     0,
  rollOff:       [0, 0],
  rolls:         [],
  round:         0,
  state:         "begin",
  turn:          0,
  underdog:      0,
  victoryPoints: [0, 0]
});

export abstract class GameRule<S extends boolean, T extends GameRuleState = GameRuleState> {
  name = "";
  dev = false;
  state: T;

  constructor(
    public player1Id: number,
    public player2Id: number,
    state: T | [string, string]
  ) {
    this.state = (state instanceof Array ? initState(state) : state) as T;
  }

  abstract begin(playerId: number, action: Action): ActionReturn<S>;
  abstract round(playerId: number, action: Action): ActionReturn<S>;

  action(playerId: number, action: Action): ActionReturn<S> {
    switch(this.state.state) {
    case "begin":
      return this.begin(playerId, action);
    case "round":
      return this.round(playerId, action);
    }
  }

  rollOffResult() {
    const { rollOff, rolls } = this.state;

    return rollOff.length === 0 ? 0 : rollOff[0] !== 1 || rollOff[1] !== 1 || rolls[0] === rolls[1] ? -1 : rolls[0] > rolls[1] ? 1 : 2;
  }

  rollOff(_playerId: number, _action: Action) {
    return null as ActionReturn<S, number>;
  }
}

export const playerInTurn = [[], [0, 1, 2], [0, 2, 1]] as const;

export const otherPlayer = (player: number) => (player === 1 ? 2 : 1);

export type GameRuleConstructor<S extends boolean, T extends GameRuleState = GameRuleState> = abstract new (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...args: any[]
) => InstanceType<typeof GameRule<S, T>>;

export type ConcreteGameRuleConstructor<S extends boolean, T extends GameRuleState = GameRuleState> = new (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...args: any[]
) => InstanceType<typeof GameRule<S, T>>;
